import {
  alpha,
  Box,
  Collapse,
  Stack,
  Tab,
  Tooltip,
  Typography,
  useTheme,
  Zoom,
} from "@mui/material";
import React, { useState, useRef, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../../../Icon/Icon";
import { AvatarGroup } from "../../../../MainComponents/Avatar/AvatarGroup";
import { InvestigationButton } from "./InvestigationButton";
import { RichTextEditor } from "../../../../MainComponents/RichTextEditor/RichTextEditor";
import { ReactComponent as EthiquinAvatar } from "../../../../../assets/images/Ethiquin/ethiquin_avatar.svg";
import { formatDateAndTime } from "../helpers/formatDate";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { useConstants } from "../Main/useConstants";
import _ from "lodash";
import { EGButton } from "../../../../MainComponents/Button/EGButton/EGButton";
import { useSelector } from "react-redux";

export const ChipParticipant = ({
  role,
  name,
  idx,
  handleDeleteParticipant,
}) => {
  const theme = useTheme();

  return (
    <Box
      display="flex"
      alignItems="center"
      border={`1px solid ${theme.palette.primary.main}`}
      borderRadius={"5px"}
      sx={{ width: "fit-content" }}
    >
      <Box paddingY={0.4} paddingX={1} sx={{ textWrap: "nowrap" }}>
        <Typography
          fontSize={14}
          fontWeight={700}
          color={theme.palette.primary.main}
          display="inline"
        >
          {role}:
        </Typography>
        <Typography
          fontSize={14}
          color={theme.palette.primary.main}
          display={"inline"}
          paddingLeft={0.4}
        >
          {name}
        </Typography>
      </Box>
      {handleDeleteParticipant && idx != null && (
        <Box
          label="X"
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 4px",
            cursor: "pointer",
          }}
          onClick={() => handleDeleteParticipant(idx)}
        >
          <Icon name="close_icon" color={theme.palette.white.main} />
        </Box>
      )}
    </Box>
  );
};

export const Separator = ({
  text,
  icon,
  iconButton,
  onClick = () => {},
  sx = {},
}) => {
  const theme = useTheme();
  return (
    <Box
      display="flex"
      alignItems="center"
      sx={{
        width: "100%",
        ...sx,
      }}
    >
      {icon && (
        <Box
          mr={1.5}
          sx={{
            border: `2px solid ${theme.palette.ethicsBlue.main}`,
            borderRadius: "50%",
            minWidth: "38px",
            minHeight: "38px",
            display: "grid",
            placeContent: "center",
          }}
        >
          <Icon name={icon} color={theme.palette.secondary.main} size={24} />
        </Box>
      )}
      <Typography
        fontWeight={600}
        fontSize={16}
        color={theme.palette.ethicsBlue.main}
        whiteSpace="nowrap"
        overflow="visible"
        flexShrink={0}
      >
        {text}
      </Typography>
      <Box
        flexGrow={1}
        height="1px"
        sx={{
          backgroundColor: theme.palette.ethicsBlue.main,
          marginLeft: 1,
          color: "transparent",
          width: "100%",
        }}
      >
        .
      </Box>
      {iconButton && onClick && (
        <InvestigationButton
          icon={iconButton}
          sx={{ marginLeft: 2 }}
          variant="primary"
          onClick={onClick}
        />
      )}
    </Box>
  );
};

// Componente para mostrar una pregunta y su respuesta (si existe) en el reporte de investigacion
export const DragAndDropCollapsable = ({
  title,
  description,
  labelCollapse,
  richTextEditor = false,
  dragHandleProps,
  handleEdit,
  handleDelete,
}) => {
  const theme = useTheme();

  const [showAnswer, setShowAnswer] = useState(false);

  return (
    <Stack
      direction="row"
      spacing={1}
      border={`1px solid ${theme.palette.blueGray200.main}`}
      borderRadius={1}
      padding={1}
    >
      <Stack direction="row" alignItems="flex-start" spacing={0.8}>
        {dragHandleProps && (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="start"
            {...dragHandleProps}
          >
            <Icon
              name="outline_move"
              size={12}
              color={theme.palette.blueGray500.main}
              style={{ cursor: "grab" }}
            />
          </Box>
        )}
        <Icon
          name="bold_arrow_down_3"
          size={12}
          color={theme.palette.blueGray500.main}
          onClick={() => setShowAnswer(!showAnswer)}
          style={{
            cursor: description ? "pointer" : "default",
            opacity: description ? 1 : 0,
            transform: showAnswer ? "rotate(0deg)" : "rotate(-90deg)",
            transition: "transform 0.2s ease-in-out",
          }}
        />
      </Stack>

      <Stack
        direction="column"
        sx={{
          flex: "1 1 auto",
          minWidth: 0,
        }}
      >
        <Typography
          fontSize={14}
          fontWeight={600}
          color={theme.palette.secondary.main}
          sx={{
            width: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {title}
        </Typography>
        {description && (
          <Collapse in={showAnswer}>
            <Box sx={{ width: "100%" }}>
              {labelCollapse && (
                <Typography
                  fontSize={14}
                  fontWeight={600}
                  color={theme.palette.blueGray600.main}
                  component="span"
                >
                  {labelCollapse}:&nbsp;
                </Typography>
              )}

              {richTextEditor ? (
                <RichTextEditor value={description} justView={true} />
              ) : (
                <Typography
                  fontSize={14}
                  color={theme.palette.oxfordBlue.main}
                  component="span"
                  fontStyle="italic"
                >
                  {description}
                </Typography>
              )}
            </Box>
          </Collapse>
        )}
      </Stack>

      <Box display="flex" alignItems="flex-start" sx={{ flexShrink: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          {handleDelete && (
            <Icon
              name="trash_can"
              size={16}
              color={theme.palette.error.main}
              style={{ cursor: "pointer" }}
              onClick={handleDelete}
            />
          )}
          {handleEdit && (
            <Icon
              name="outline_edit"
              size={16}
              color={theme.palette.primary.main}
              style={{ cursor: "pointer" }}
              onClick={handleEdit}
            />
          )}
        </Stack>
      </Box>
    </Stack>
  );
};

// Componente para arrastrar y soltar archivos o seleccionar desde el explorador
export const DragAndDropFiles = ({
  text = "General.drag_and_drop",
  onFileSelect,
  multiple = false,
  isValid = null,
  disabled = false,
}) => {
  const theme = useTheme();
  const [t] = useTranslation("investigation");
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    if (disabled) return;
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    if (disabled) return;
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (disabled) return;
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      if (multiple) {
        onFileSelect(Array.from(files));
      } else {
        onFileSelect(files[0]);
      }
    }
  };

  const handleClick = () => {
    if (disabled) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    if (disabled) return;
    const files = e.target.files;
    if (files.length > 0) {
      if (multiple) {
        onFileSelect(Array.from(files));
      } else {
        onFileSelect(files[0]);
      }
    }
  };

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        multiple={multiple}
        style={{ display: "none" }}
      />
      <Box
        sx={{
          filter: disabled ? "opacity(0.4)" : "none",
          border: `2px dashed ${
            isDragging
              ? theme.palette.primary.main
              : theme.palette.blueGray200.main
          }`,
          borderRadius: "4px",
          p: 1.6,
          textAlign: "center",
          cursor: disabled ? "not-allowed" : "pointer",
          backgroundColor: isDragging
            ? `${theme.palette.primary.main}10`
            : "transparent",
          transition: "all 0.2s ease-in-out",
          ...(isValid === true
            ? {
                borderColor: theme.palette.success.main,
              }
            : isValid === false
            ? {
                borderColor: theme.palette.error.main,
              }
            : {
                borderColor: theme.palette.blueGray200.main,
              }),
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <Typography color="secondary" fontSize={16} fontWeight={600}>
          {t("General.drag_and_drop")}
        </Typography>
        <Box my={1.3}>
          <Icon
            name="outline_export"
            size={40}
            color={theme.palette.secondary.main}
          />
        </Box>
        <Typography color="secondary">{t("General.or_click_here")}</Typography>
      </Box>
    </>
  );
};

// Muesta un archivo subido temporalmente, por ejemplo, antes de guardarlo en la base de datos
export const TemporalUploadedFile = ({
  name,
  size,
  description,
  iconName,
  iconColor = "#ECECEC",
  iconAction,
  width = "fit-content",
}) => {
  const theme = useTheme();

  const formatFileSize = (bytes) => {
    if (bytes == null) return "";
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.teal.main}`,
        borderRadius: "5px",
        p: 1,
        width,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{ width: 24, height: 24 }}>
            <Icon
              name="bold_attach_circle"
              size={24}
              color={theme.palette.teal.main}
            />
          </Box>
          <Stack>
            <Typography color="text.primary" fontWeight={500}>
              {name} {size != null ? `(${formatFileSize(size)})` : ""}
            </Typography>
          </Stack>
        </Stack>
        {iconName && (
          <Box sx={{ width: 24, height: 24 }}>
            <Icon
              name={iconName}
              color={iconColor}
              style={{ cursor: iconAction ? "pointer" : "default" }}
              onClick={iconAction ? iconAction : undefined}
              size={16}
            />
          </Box>
        )}
      </Stack>
      {description && (
        <Typography
          fontSize={12}
          fontStyle="italic"
          color={theme.palette.secondary.main}
        >
          {description}
        </Typography>
      )}
    </Box>
  );
};

// Muestra un archivo que ya ha sido subido y guardado en la base de datos
export const UploadedFile = ({
  text = "",
  description,
  downloadUrl,
  preview,
  width = "100%",
  sx = {},
}) => {
  const theme = useTheme();

  return (
    <Stack
      padding={0.5}
      border={`1px solid ${theme.palette.blueGray200.main}`}
      borderRadius={1}
      justifyContent="space-between"
      width={width}
      sx={{ ...sx }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={1}
      >
        <Typography
          fontSize={12.5}
          fontWeight={600}
          color="secondary.main"
          mt={0.5}
        >
          {text}
        </Typography>
        <Stack direction="row" spacing={0.5}>
          {preview && (
            <InvestigationButton
              icon={"outline_eye"}
              variant="primary"
              size="thin"
            />
          )}

          <a
            href={downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            download
          >
            <InvestigationButton
              icon={"outline_import"}
              variant="primary"
              size="thin"
            />
          </a>
        </Stack>
      </Stack>
      {description && (
        <Typography
          fontSize={12}
          fontStyle="italic"
          color={theme.palette.blueGray.main}
        >
          {description}
        </Typography>
      )}
    </Stack>
  );
};

export const GenerateWithIA = ({
  text = "Generate with AI",
  buttonText = "GENERATE",
  onClick = () => {},
  style = {},
  isLoading = false,
  disabled = false,
}) => {
  const theme = useTheme();
  return (
    <Stack direction="row" alignItems="center" style={style}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        sx={{
          width: 40,
          height: 40,
          border: `1px solid ${theme.palette.secondary.main}`,
          borderRadius: "50%",
          mr: 1.5,
        }}
      >
        <EthiquinAvatar />
      </Box>
      <Stack
        border={`1px solid ${theme.palette.primary.main}`}
        boxShadow={`0px 0px 4px 1px ${alpha(theme.palette.primary.main, 0.5)}`}
        p={0.5}
        position="relative"
        spacing={1.5}
        borderRadius={2}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexFlow: "row wrap",
          "&::before": {
            content: '""',
            position: "absolute",
            left: "-12px",
            top: "50%",
            transform: "translateY(-50%)",
            width: 0,
            height: 0,
            borderTop: "8px solid transparent",
            borderBottom: "8px solid transparent",
            borderRight: `12px solid ${theme.palette.primary.main}`,
          },
          "&::after": {
            content: '""',
            position: "absolute",
            left: "-11px",
            top: "50%",
            transform: "translateY(-50%)",
            width: 0,
            height: 0,
            borderTop: "8px solid transparent",
            borderBottom: "8px solid transparent",
            borderRight: "12px solid white",
          },
        }}
      >
        <Typography
          fontSize={13}
          color="black.main"
          textAlign="center"
          px={0.5}
          sx={{
            flexGrow: 1,
            minWidth: 0,
            whiteSpace: "pre-wrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {text}
        </Typography>
        <EGButton
          disableElevation={false}
          label={buttonText}
          iconName="outline_ai"
          iconSize={20}
          color="primary"
          loading={isLoading}
          onClick={onClick}
          disabled={disabled}
          sx={{ mb: { xs: 0.5, sm: 0 } }}
        />
      </Stack>
    </Stack>
  );
};

export const MUITabs = ({ tabs, value, onChange, defaultTab }) => {
  const [currentValue, setCurrentValue] = useState(defaultTab);
  const theme = useTheme();

  useEffect(() => {
    if (value) {
      setCurrentValue(value);
    }
  }, [value]);

  return (
    <TabContext value={currentValue}>
      <Box marginBottom={2}>
        <TabList
          onChange={(_, value) => {
            setCurrentValue(value);
            onChange(value);
          }}
          aria-label="lab API tabs example"
          sx={{
            "& .MuiTab-root": {
              fontFamily: "Inter, sans-serif",
              fontSize: "14px",
              fontWeight: 500,
              textTransform: "none",
              minHeight: "48px",
              color: theme.palette.blueGray600.main,
              "&.Mui-selected": {
                color: theme.palette.primary.main,
                fontWeight: 600,
              },
            },
            "& .MuiTabs-indicator": {
              height: "3px",
              backgroundColor: theme.palette.primary.main,
            },
          }}
        >
          {tabs.map((tab) => (
            <Tab key={tab.value} label={tab.label} value={tab.value} />
          ))}
        </TabList>
      </Box>
      {tabs.map((tab) => (
        <TabPanel
          key={tab.value}
          value={tab.value}
          sx={{
            padding: 0,
          }}
        >
          {tab.component}
        </TabPanel>
      ))}
    </TabContext>
  );
};

// Plan de actividades
// Componente para mostrar una pregunta y su respuesta (si existe) en el reporte de investigacion
/**
 * @param {Object} props - Los parametros del componente
 * @param {React.ReactNode} props.title - El titulo de la pregunta
 * @param {React.ReactNode} props.details - La respuesta de la pregunta
 * @param {React.ReactNode} props.secondCollapse - La respuesta de la pregunta
 * @param {Array<{
 *   name: string,
 *   onClick: () => void,
 *   color: string,
 *   size: number,
 *   style: React.CSSProperties,
 *   tooltip: string,
 * }>} props.buttons - Los botones que se mostrarán en el componente
 * @returns
 */
export const InvestigationCollapsable = ({
  title,
  details,
  secondCollapse,
  showSecondCollapse,
  dragHandleProps,
  buttons = [], // tooltip, name, color, onClick, style
}) => {
  const theme = useTheme();

  const [showDetails, setShowDetails] = useState(false);

  return (
    <Stack
      direction="row"
      spacing={1}
      border={`1px solid ${theme.palette.blueGray200.main}`}
      borderRadius={1}
      padding={1}
    >
      <Stack
        direction="row"
        spacing={0.8}
        alignItems="flex-start"
        justifyContent="flex-start"
        marginTop={0.6}
      >
        {dragHandleProps && (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="start"
            {...dragHandleProps}
          >
            <Icon
              name="outline_move"
              size={12}
              color={theme.palette.blueGray500.main}
              style={{ cursor: "grab" }}
            />
          </Box>
        )}
        <Icon
          name="bold_arrow_down_3"
          size={12}
          color={theme.palette.blueGray500.main}
          onClick={details ? () => setShowDetails(!showDetails) : undefined}
          style={{
            cursor: details ? "pointer" : "default",
            opacity: details ? 1 : 0,
            transform: showDetails ? "rotate(0deg)" : "rotate(-90deg)",
            transition: "transform 0.2s ease-in-out",
          }}
        />
      </Stack>

      <Stack
        direction="column"
        sx={{
          flex: "1 1 auto",
          minWidth: 0,
        }}
      >
        <Stack direction="row" spacing={1}>
          <Box width="100%" overflow="hidden" textOverflow="ellipsis">
            {title}
          </Box>
          {buttons && buttons.length > 0 && (
            <Box
              display="flex"
              alignItems="flex-start"
              sx={{ flexShrink: 0, mt: 0.5 }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                {buttons.map((button, i) => (
                  <Tooltip
                    key={i}
                    title={button.tooltip}
                    placement="top"
                    arrow
                    TransitionComponent={Zoom}
                    TransitionProps={{ timeout: 150 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "center",
                      }}
                    >
                      <Icon
                        key={button.name}
                        name={button.name}
                        size={16}
                        color={button.color}
                        style={{ cursor: "pointer", ...button.style }}
                        onClick={button.onClick}
                      />
                    </Box>
                  </Tooltip>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>

        {details && (
          <Collapse in={showDetails}>
            <Box>
              <div
                className="hr-div"
                style={{ marginBottom: 8, marginTop: 8 }}
              />
              {details}
            </Box>
          </Collapse>
        )}

        <Collapse in={showSecondCollapse != null ? showSecondCollapse : false}>
          <Box sx={{ width: "100%" }}>{secondCollapse}</Box>
        </Collapse>
      </Stack>
    </Stack>
  );
};

export const DetailsTodo = ({ todo }) => {
  const [t] = useTranslation("investigation");
  const { functions: { getUserLabel } } = useConstants();

  const dateDeadline = useMemo(
    () =>
      formatDateAndTime(
        todo.deadline,
        todo.timeDeadline,
        t("activityPlan.not_assigned")
      ),
    [todo.deadline, todo.timeDeadline]
  );

  return (
    <>
      {todo.assignedTo?.id && todo.assignedTo?.from && (
        <Typography color="blueGray600.main" fontSize={11} fontWeight={600}>
          {t("activityPlan.responsible_of_the_activity")}:&nbsp;
          <Typography
            color="oxfordBlue.main"
            component="span"
            fontSize={11}
            fontStyle="italic"
          >
            {getUserLabel(todo.assignedTo) || t("activityPlan.user_not_found")}
          </Typography>
        </Typography>
      )}
      {todo.details && (
        <Typography color="blueGray600.main" fontSize={11} fontWeight={600}>
          {t("activityPlan.details_of_the_activity")}:&nbsp;
          <Typography
            color="oxfordBlue.main"
            component="span"
            fontSize={11}
            fontStyle="italic"
          >
            {todo.details || t("activityPlan.not_assigned")}
          </Typography>
        </Typography>
      )}
      {todo.deadline && (
        <Typography color="blueGray600.main" fontSize={11} fontWeight={600}>
          {t("activityPlan.deadline")}:&nbsp;
          <Typography
            color="oxfordBlue.main"
            component="span"
            fontSize={11}
            fontStyle="italic"
          >
            {dateDeadline || t("activityPlan.not_assigned")}
          </Typography>
        </Typography>
      )}
      {todo.dedicatedHours && (
        <Typography color="blueGray600.main" fontSize={11} fontWeight={600}>
          {t("activityPlan.dedicated_hours")}:&nbsp;
          <Typography
            color="oxfordBlue.main"
            component="span"
            fontSize={11}
            fontStyle="italic"
          >
            {todo.dedicatedHours || t("activityPlan.not_assigned")}
          </Typography>
        </Typography>
      )}
      {todo.party?.length > 0 && (
        <Typography color="blueGray600.main" fontSize={11} fontWeight={600}>
          {t("activityPlan.participants")}:&nbsp;
          <Typography
            color="oxfordBlue.main"
            component="span"
            fontSize={11}
            fontStyle="italic"
          >
            {todo.party?.length > 0
              ? todo.party
                  .map(
                    (participant) =>
                      getUserLabel(participant) ||
                      t("activityPlan.user_not_found")
                  )
                  .join(", ")
              : t("activityPlan.not_assigned")}
          </Typography>
        </Typography>
      )}
    </>
  );
};

export const Avatars = ({ assignedTo, party }) => {
  const { functions: { getUserLabel } } = useConstants();

  const contacts = useMemo(() => {
    const finalContacts = [];
    if (assignedTo) {
      finalContacts.push({ name: getUserLabel(assignedTo) || assignedTo.id });
    }
    if (Array.isArray(party) && party.length > 0) {
      const partyContacts = party.map((p) => {
        if (typeof p === "string") return { name: p, email: p };
        return { name: getUserLabel(p) || p.id };
      });
      finalContacts.push(...partyContacts);
    }
    return finalContacts;
  }, [assignedTo, party]);

  return <AvatarGroup contacts={contacts} max={3} />;
};
